/**
 * Heures habituelles de l'entreprise (Mon entreprise → Activité → Horaires) :
 * jours travaillés, début et fin de la journée. La réaffectation du planning
 * prévient quand un créneau en sort (soir, nuit, jour non travaillé) ; c'est
 * un avertissement, jamais un refus (Q08).
 *
 * Défaut, quand l'entreprise n'a rien réglé : du lundi au vendredi, de 7:00 à
 * 19:00. Assez large pour les départs matinaux et les fins de journée du
 * dépannage, assez étroit pour signaler une soirée, une nuit ou un week-end.
 */

export interface WorkingHours {
  /** « 07:00 » : début de la journée habituelle. */
  start: string;
  /** « 19:00 » : fin de la journée habituelle. */
  end: string;
  /** Jours travaillés, ISO (1 = lundi … 7 = dimanche), triés. */
  days: number[];
}

export const DEFAULT_WORKING_HOURS: Readonly<WorkingHours> = Object.freeze({
  start: '07:00',
  end: '19:00',
  days: [1, 2, 3, 4, 5],
});

/** Jours ISO dans l'ordre de la semaine, avec leur nom court et complet. */
export const WORKING_DAYS: readonly { day: number; short: string; label: string }[] = [
  { day: 1, short: 'lun.', label: 'Lundi' },
  { day: 2, short: 'mar.', label: 'Mardi' },
  { day: 3, short: 'mer.', label: 'Mercredi' },
  { day: 4, short: 'jeu.', label: 'Jeudi' },
  { day: 5, short: 'ven.', label: 'Vendredi' },
  { day: 6, short: 'sam.', label: 'Samedi' },
  { day: 7, short: 'dim.', label: 'Dimanche' },
];

const CLOCK = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** « 07:30 » est-elle une heure d'horloge valide (00:00 à 23:59) ? */
export function isClock(value: unknown): value is string {
  return typeof value === 'string' && CLOCK.test(value);
}

/** Minutes depuis minuit d'une heure « HH:MM ». */
export function clockMinutes(clock: string): number {
  const [hours, minutes] = clock.split(':').map(Number);
  return (hours ?? 0) * 60 + (minutes ?? 0);
}

/**
 * Ce qui empêche d'enregistrer ces heures, ou null. Mêmes règles pour l'écran
 * et pour l'API : au moins un jour, des heures valides, la fin après le début.
 */
export function workingHoursProblem(value: WorkingHours): string | null {
  if (!isClock(value.start) || !isClock(value.end)) return 'Heure invalide (format 07:30).';
  if (clockMinutes(value.end) <= clockMinutes(value.start)) return 'La fin doit être après le début.';
  if (value.days.length === 0) return 'Choisissez au moins un jour travaillé.';
  if (value.days.some((day) => !Number.isInteger(day) || day < 1 || day > 7)) return 'Jour inconnu.';
  if (new Set(value.days).size !== value.days.length) return 'Jour en double.';
  return null;
}

/**
 * Lecture tolérante d'une valeur enregistrée : une valeur absente ou illisible
 * rend le défaut, jamais une erreur (le planning doit toujours répondre).
 */
export function readWorkingHours(value: unknown): WorkingHours {
  if (!value || typeof value !== 'object') return { ...DEFAULT_WORKING_HOURS, days: [...DEFAULT_WORKING_HOURS.days] };
  const raw = value as Partial<Record<keyof WorkingHours, unknown>>;
  const candidate: WorkingHours = {
    start: typeof raw.start === 'string' ? raw.start : '',
    end: typeof raw.end === 'string' ? raw.end : '',
    days: Array.isArray(raw.days) ? raw.days.filter((day): day is number => typeof day === 'number') : [],
  };
  if (workingHoursProblem(candidate)) return { ...DEFAULT_WORKING_HOURS, days: [...DEFAULT_WORKING_HOURS.days] };
  return { ...candidate, days: [...candidate.days].sort((a, b) => a - b) };
}

/** « lun.–ven., 7:00–19:00 » : les heures habituelles en une ligne lisible. */
export function workingHoursSummary(hours: WorkingHours): string {
  const days = [...hours.days].sort((a, b) => a - b);
  const consecutive = days.every((day, index) => index === 0 || day === days[index - 1]! + 1);
  const name = (day: number): string => WORKING_DAYS.find((item) => item.day === day)?.short ?? '';
  const dayText = consecutive && days.length > 2
    ? `${name(days[0]!)}–${name(days.at(-1)!)}`
    : days.map(name).join(', ');
  const clock = (value: string): string => value.replace(/^0(\d)/, '$1');
  return `${dayText}, ${clock(hours.start)}–${clock(hours.end)}`;
}
